import { create } from "zustand";
import { purgeVolatileCaches } from "@/lib/security";
import type {
  DashboardData,
  Divergencia,
  NotaSimplificada,
  NotaStatus,
  PipelinePhase,
  PipelineState,
} from "@/lib/fiscal-types";

interface PhaseProgress {
  phase: PipelinePhase;
  current: number;
  total: number;
  batch?: number;
}

export interface UploadRecord {
  id: string;
  fileName: string;
  fileSize: number;
  totalNotas: number;
  totalDivergencias: number;
  duracaoMs: number;
  timestamp: string;
  status: "success" | "error";
}

interface FiscalStore {
  state: PipelineState;
  message: string;
  phase: PipelinePhase;
  progress: Record<PipelinePhase, PhaseProgress>;
  fileName: string | null;
  fileSize: number;
  duracaoMs: number;
  dashboard: DashboardData | null;
  notas: NotaSimplificada[];
  divergencias: Divergencia[];
  error: string | null;
  filtroStatus: NotaStatus | null;
  uploadHistory: UploadRecord[];
  privacyMode: boolean;
  setFiltroStatus: (s: NotaStatus | null) => void;
  togglePrivacyMode: () => void;
  set: (p: Partial<FiscalStore>) => void;
  setProgress: (p: PhaseProgress) => void;
  addUploadRecord: (r: UploadRecord) => void;
  applyAutoCorrections: () => { corrigidas: number; restantes: number };
  reset: () => void;
  /**
   * LGPD Art. 18 — Eliminação. Destrói todos os dados fiscais em memória
   * e limpa caches voláteis (sessionStorage). Deve ser invocado no logout.
   */
  purgeAllData: () => void;
}

const emptyProgress: Record<PipelinePhase, PhaseProgress> = {
  upload: { phase: "upload", current: 0, total: 0 },
  extract: { phase: "extract", current: 0, total: 0 },
  process: { phase: "process", current: 0, total: 0 },
  consolidate: { phase: "consolidate", current: 0, total: 0 },
};

const initial = {
  state: "idle" as PipelineState,
  message: "",
  phase: "upload" as PipelinePhase,
  progress: emptyProgress,
  fileName: null,
  fileSize: 0,
  duracaoMs: 0,
  dashboard: null,
  notas: [],
  divergencias: [],
  error: null,
  filtroStatus: null as NotaStatus | null,
  uploadHistory: [] as UploadRecord[],
  privacyMode: false,
};

export const useFiscalStore = create<FiscalStore>((set, get) => ({
  ...initial,
  setFiltroStatus: (s) => set({ filtroStatus: s }),
  togglePrivacyMode: () => set((s) => ({ privacyMode: !s.privacyMode })),
  set: (p) => set(p),
  setProgress: (p) =>
    set((s) => ({
      phase: p.phase,
      progress: { ...s.progress, [p.phase]: p },
    })),
  addUploadRecord: (r) =>
    set((s) => ({ uploadHistory: [r, ...s.uploadHistory].slice(0, 50) })),
  applyAutoCorrections: () => {
    const { divergencias, notas, dashboard } = get();
    if (!dashboard) return { corrigidas: 0, restantes: 0 };

    const TOL = 0.01;
    const chavesComDiv = new Set(divergencias.map((d) => d.chave));
    let corrigidasCount = 0;

    const novasNotas = notas.map((n) => {
      if (!chavesComDiv.has(n.chave) && n.divergencia == null) return n;

      const declarado = n.valorDeclarado ?? n.valorOficial;
      let calculado = n.valorItens;
      let diff = Math.abs(calculado - declarado);

      if (diff > TOL) {
        calculado = declarado;
        diff = 0;
      }

      const valorFinal = declarado;
      corrigidasCount++;
      return {
        ...n,
        valorItens: calculado,
        valorOficial: valorFinal,
        divergencia: null,
        status: null,
      };
    });

    const totalRevenue = novasNotas.reduce(
      (s, n) => (n.notaStatus === "AUTORIZADA" || n.notaStatus === "CONTINGENCIA" ? s + (n.valorOficial || 0) : s),
      0
    );
    const risk = { alta: 0, media: 0, baixa: 0 };
    for (const n of novasNotas) {
      if (n.notaStatus !== "AUTORIZADA" && n.notaStatus !== "CONTINGENCIA") continue;
      if (n.divergencia === "ALTA") risk.alta++;
      else if (n.divergencia === "MEDIA") risk.media++;
      else if (n.divergencia === "BAIXA") risk.baixa++;
    }

    set({
      divergencias: [],
      notas: novasNotas,
      dashboard: {
        ...dashboard,
        stats: { ...dashboard.stats, totalRevenue },
        riskSummary: risk,
      },
    });

    return { corrigidas: corrigidasCount, restantes: 0 };
  },
  reset: () => set({ ...initial, progress: { ...emptyProgress }, uploadHistory: get().uploadHistory }),
  purgeAllData: () => {
    set({ ...initial, progress: { ...emptyProgress } });
    purgeVolatileCaches();
  },
}));
